import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recsAPI } from '../api';
import { Sparkles, Clock, ChevronRight, TrendingUp } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';

// Map our internal category keys -> display category names so the catalog page works
const navigateToProduct = (navigate, item) => {
  if (item.category) {
    navigate('/product-catalog', { state: { category: item.category, subcategory: item.subcategory } });
  } else {
    navigate('/category-selection');
  }
};

const ProductCard = ({ item, onClick, dataTestId }) => {
  const meta = item.metadata || {};
  const name = meta.name || item.product_id || 'Featured';
  const img = meta.image || meta.thumbnail;
  const price = meta.price;
  return (
    <button
      data-testid={dataTestId}
      onClick={onClick}
      className="flex-shrink-0 w-40 text-left group"
    >
      <div className="relative aspect-[3/4] bg-white border overflow-hidden mb-2"
        style={{ borderColor: LINE }}>
        {img ? (
          <img src={img} alt={name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #FAF9F6 0%, #F0EFEC 100%)' }}>
            <Sparkles className="w-6 h-6" style={{ color: GOLD }} strokeWidth={1.2} />
          </div>
        )}
      </div>
      <p className="text-[12px] truncate" style={{ color: INK, fontWeight: 500 }}>{name}</p>
      {item.category && (
        <p className="text-[10px] capitalize" style={{ color: MUTED }}>{item.category}</p>
      )}
      {price && (
        <p className="text-[12px] mt-0.5" style={{ color: GOLD, fontFamily: 'Playfair Display, serif' }}>
          ₹{price}
        </p>
      )}
    </button>
  );
};

const SectionHeader = ({ icon: Icon, title, sub, onSeeAll }) => (
  <div className="flex items-end justify-between mb-3 px-4">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" style={{ color: GOLD }} strokeWidth={1.5} />
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase" style={{ color: GOLD }}>{title}</p>
        {sub && <p className="text-[10px]" style={{ color: MUTED }}>{sub}</p>}
      </div>
    </div>
    {onSeeAll && (
      <button onClick={onSeeAll} className="flex items-center gap-1 text-[10px] tracking-[0.08em] uppercase" style={{ color: MUTED }}>
        Browse <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
      </button>
    )}
  </div>
);

export const ForYouSection = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = isLoggedIn ? await recsAPI.me(8) : await recsAPI.trending(14, 8);
        if (mounted) setFeed(data.products ? data : { products: data.items || [], strategy: 'trending' });
      } catch {
        if (mounted) setFeed({ products: [], strategy: 'error' });
      }
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [isLoggedIn]);

  if (loading) return null;
  const products = feed?.products || [];
  if (products.length === 0) return null;

  const isPersonalized = feed?.strategy === 'personalized' && (feed?.categories?.length || 0) > 0;

  return (
    <div data-testid="for-you-section" className="py-6" style={{ borderTop: `1px solid ${LINE}` }}>
      <SectionHeader
        icon={isPersonalized ? Sparkles : TrendingUp}
        title={isPersonalized ? 'For You' : 'Trending Now'}
        sub={isPersonalized ? `Picked from your interests · ${feed.categories.slice(0, 2).map(c => c.name).join(' · ')}` : 'What everyone is loving'}
        onSeeAll={() => navigate('/category-selection')}
      />
      <div className="overflow-x-auto scrollbar-hide pl-4">
        <div className="flex gap-3 pr-4">
          {products.map((p, i) => (
            <ProductCard key={`${p.product_id}-${i}`} item={p}
              dataTestId={`for-you-item-${i}`}
              onClick={() => navigateToProduct(navigate, p)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const RecentlyViewedSection = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await recsAPI.recentlyViewed(12);
        if (mounted) setItems(data.items || []);
      } catch {}
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <div data-testid="recently-viewed-section" className="py-6" style={{ borderTop: `1px solid ${LINE}` }}>
      <SectionHeader icon={Clock} title="Recently Viewed" sub="Pick up where you left off" />
      <div className="overflow-x-auto scrollbar-hide pl-4">
        <div className="flex gap-3 pr-4">
          {items.map((p, i) => (
            <ProductCard key={`${p.product_id}-${i}`} item={p}
              dataTestId={`recently-viewed-item-${i}`}
              onClick={() => navigateToProduct(navigate, p)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const SimilarProductsSection = ({ productId }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      try {
        const data = await recsAPI.similar(productId, 8);
        if (mounted) setItems(data.items || []);
      } catch {}
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [productId]);

  if (loading || items.length === 0) return null;
  return (
    <div data-testid="similar-products-section" className="py-6" style={{ borderTop: `1px solid ${LINE}` }}>
      <SectionHeader icon={Sparkles} title="You May Also Like" sub="Customers who viewed this also explored" />
      <div className="overflow-x-auto scrollbar-hide pl-4">
        <div className="flex gap-3 pr-4">
          {items.map((p, i) => (
            <ProductCard key={`${p.product_id}-${i}`} item={p}
              dataTestId={`similar-item-${i}`}
              onClick={() => navigateToProduct(navigate, p)} />
          ))}
        </div>
      </div>
    </div>
  );
};
